// Turns protocol frames into what the log view draws: lines, container/Job boundaries and the outcome.
import type { Frame, LogEvent, Summary } from './logStream';

export type Entry =
  | { id: number; kind: 'line'; container: string; ts: string | null; text: string; truncated?: boolean }
  | { id: number; kind: 'boundary'; text: string }
  | { id: number; kind: 'meta'; text: string }
  | { id: number; kind: 'outcome'; ok: boolean; text: string };

export interface LogModel {
  entries: Entry[];
  /** Lines dropped from the front to keep the view bounded. */
  dropped: number;
  job?: { job: string; step: string };
  container?: string;
  outcome?: { ok: boolean; text: string; summary?: Summary };
  state: string;
  sentence?: string;
}

export const MAX_ENTRIES = 5000;

export function emptyModel(): LogModel {
  return { entries: [], dropped: 0, state: 'connecting' };
}

type NoId<T> = T extends unknown ? Omit<T, 'id'> : never;

let seq = 0;
function push(m: LogModel, e: NoId<Entry>): void {
  m.entries.push({ ...(e as Entry), id: ++seq });
  if (m.entries.length > MAX_ENTRIES) {
    const n = m.entries.length - MAX_ENTRIES;
    m.entries.splice(0, n);
    m.dropped += n;
  }
}

function summaryText(s: Summary): string {
  const parts: string[] = [];
  if (s.phase) parts.push(`phase ${s.phase}`);
  if (s.lastAction) {
    const a = s.lastAction;
    parts.push(`last action ${a.type} ${a.outcome}${a.reason ? ` (${a.reason})` : ''}${a.message ? `: ${a.message}` : ''}`);
  }
  const stalled = s.conditions.find((c) => c.type === 'Stalled' && c.status === 'True');
  if (stalled) parts.push(`Stalled: ${stalled.reason}: ${stalled.message}`);
  return parts.join('; ');
}

export function apply(m: LogModel, ev: LogEvent): void {
  if (ev.kind === 'state') {
    const was = m.state;
    m.state = ev.state;
    m.sentence = ev.sentence;
    if (ev.state === 'reconnecting' && ev.sentence) push(m, { kind: 'meta', text: `— ${ev.sentence}` });
    if (ev.state === 'open' && was === 'reconnecting') push(m, { kind: 'meta', text: '— reconnected' });
    if (ev.state === 'failed' && ev.sentence) push(m, { kind: 'meta', text: `— ${ev.sentence}` });
    return;
  }
  const f: Frame = ev.frame;
  switch (f.type) {
    case 'ready':
      m.outcome = undefined;
      break;
    case 'job':
      m.job = { job: f.job, step: f.step };
      push(m, { kind: 'boundary', text: `▶ step ${f.step} — Job ${f.job}` });
      break;
    case 'pod':
      push(m, { kind: 'meta', text: `pod ${f.pod}${f.attempt > 1 ? ` (attempt ${f.attempt})` : ''}` });
      break;
    case 'container':
      m.container = f.container;
      push(m, { kind: 'boundary', text: `┌ ${f.init ? 'init container' : 'container'} ${f.container}` });
      break;
    case 'line':
      push(m, { kind: 'line', container: f.container, ts: f.ts, text: f.text, truncated: f.truncated });
      break;
    case 'job-end': {
      const ok = f.result === 'Succeeded';
      const detail = [f.reason, f.exitCode !== undefined && f.exitCode !== null ? `exit ${f.exitCode}` : '', f.container ? `in ${f.container}` : '', f.message].filter(Boolean).join(', ');
      push(m, { kind: 'outcome', ok, text: `${ok ? '✓' : f.result === 'Failed' ? '✕' : '?'} step ${f.step} ${f.result}${detail ? ` — ${detail}` : ''}` });
      break;
    }
    case 'waiting':
      push(m, { kind: 'meta', text: `waiting for the next Job (phase ${f.phase})…` });
      break;
    case 'idle':
    case 'end': {
      const s = summaryText(f);
      const failed = f.lastAction?.outcome === 'Failed' || f.conditions.some((c) => c.type === 'Stalled' && c.status === 'True');
      const text = f.type === 'idle' ? `No Job is running for ${f.environment}.${s ? ` ${s}.` : ''}` : `Stream ended.${s ? ` ${s}.` : ''}`;
      m.outcome = { ok: !failed, text, summary: f };
      m.state = 'ended';
      const last = m.entries.at(-1);
      // a retried idle stream says the same again: say it once
      if (!(last?.kind === 'outcome' && last.text === text)) push(m, { kind: 'outcome', ok: !failed, text });
      break;
    }
  }
}
